/**
 * Click-handler factory for the WBS row's editable cells.
 *
 * The WBS row has a top-level `onClick` that opens the right-hand
 * detail panel via `onSelect(itemId)`. For ordinary cells (name span,
 * date columns, blank percent cell) we want that selection to fire
 * when the user clicks anywhere in the row — that's the existing
 * "click row to open details" interaction.
 *
 * For dropdown-style cells (領域 / ベンダー / 担当 / ステータス) the
 * user's intent is to open the inline picker, NOT to also pop the
 * detail panel on the right. Without `stopPropagation`, clicking those
 * chips would simultaneously open the picker AND slide in the detail
 * panel — visually noisy and unwanted when the panel is currently
 * closed. (If the panel is already open we leave `selectedId` alone,
 * so it stays open naturally.)
 *
 * Keeping this factory in its own file makes the propagation contract
 * unit-testable in Vitest's `node` environment without pulling in
 * jsdom: tests pass a stub MouseEvent and assert that
 * `stopPropagation` is / is not invoked.
 */
import type { MouseEvent } from 'react';

/**
 * Build the `onClick` callback for a `<EditableCell>` wrapper.
 *
 * @param onOpen          The cell's "open inline editor" handler. Always invoked.
 * @param suppressRowSelect When true, the row's `onClick` is suppressed via
 *                          `e.stopPropagation()` BEFORE `onOpen` runs. Pass
 *                          `true` for dropdown cells; pass `false` (or omit)
 *                          for cells where opening the detail panel on click
 *                          is desirable (date columns, etc.).
 */
export function createEditableCellClickHandler(
  onOpen: (e: MouseEvent) => void,
  suppressRowSelect: boolean,
): (e: MouseEvent) => void {
  return (e) => {
    if (suppressRowSelect) {
      e.stopPropagation();
    }
    onOpen(e);
  };
}
